/**
 * Primary-body picker for multi-pose detection results.
 *
 * MediaPipe's pose landmarker returns one entry per detected person.
 * Mirrors / reflections / background bystanders / partial entrants
 * regularly land in that list alongside the actual subject. Saving
 * all of them confuses every downstream module that assumes a single
 * subject (smoothers, ROM core, biomech metrics, file viewer).
 *
 * pickPrimaryRawPose() ranks the poses by mean visibility of the
 * eight torso/limb landmarks that always exist in MediaPipe-33 and
 * COCO-17, and returns the best one. Returns null on an empty input.
 *
 * The function is pure and adapter-agnostic — call it from PC live
 * inference, mobile precompute, the crop pipeline, and any future
 * paths that want a single primary subject.
 */

import type { RawLandmark, RawPose, RawPoseResult } from './PoseEngine';

/** Native landmark indices used to rank candidate poses. Picked so the
 *  same offsets work for both MediaPipe-33 and MoveNet/COCO-17:
 *   - 11 / 12 / 23 / 24 in MediaPipe-33 = shoulders + hips
 *   - 5  / 6  / 11 / 12 in MoveNet-17    = shoulders + hips
 *   - 13 / 14 / 25 / 26 in MediaPipe-33 = elbows + knees
 *   - 7  / 8  / 13 / 14 in MoveNet-17    = elbows + knees
 *  We accept either set because indices >= numLandmarks are skipped. */
const TORSO_INDICES_MP33 = [11, 12, 23, 24, 13, 14, 25, 26] as const;
const TORSO_INDICES_COCO17 = [5, 6, 11, 12, 7, 8, 13, 14] as const;

function meanVisibility(pose: RawPose): number {
  const lms = pose.landmarks;
  if (lms.length === 0) return 0;
  const indices = lms.length >= 25 ? TORSO_INDICES_MP33 : TORSO_INDICES_COCO17;
  let sum = 0;
  let count = 0;
  for (const idx of indices) {
    if (idx >= lms.length) continue;
    const v = scoreOf(lms[idx]);
    if (Number.isFinite(v)) {
      sum += v;
      count += 1;
    }
  }
  if (count > 0) return sum / count;
  // Fallback: mean over every landmark when the torso indices were
  // unavailable (e.g. an exotic skeleton variant slipped through).
  let totalSum = 0;
  let totalCount = 0;
  for (const lm of lms) {
    const v = scoreOf(lm);
    if (Number.isFinite(v)) {
      totalSum += v;
      totalCount += 1;
    }
  }
  return totalCount > 0 ? totalSum / totalCount : 0;
}

function scoreOf(lm: RawLandmark): number {
  if (typeof lm.worldVisibility === 'number') return lm.worldVisibility;
  return lm.score;
}

export interface PickPrimaryDiagnostics {
  /** Number of candidate poses inspected. */
  candidates: number;
  /** Mean visibility of the picked pose; null when no pick. */
  pickedScore: number | null;
  /** Mean visibility of the runner-up; null when fewer than two
   *  candidates. Useful for telling "single clear subject" from
   *  "two roughly-equal candidates" in logs. */
  runnerUpScore: number | null;
  /** Index of the picked pose in the original raw.poses array,
   *  null when no pick. */
  pickedIndex: number | null;
}

/**
 * Pick the most plausible primary subject from a multi-pose result.
 * Returns the chosen RawPose and diagnostics describing the call.
 */
export function pickPrimaryRawPose(
  raw: RawPoseResult,
): { pose: RawPose | null; diagnostics: PickPrimaryDiagnostics } {
  const candidates = raw.poses.length;
  if (candidates === 0) {
    return {
      pose: null,
      diagnostics: { candidates: 0, pickedScore: null, runnerUpScore: null, pickedIndex: null },
    };
  }
  if (candidates === 1) {
    const score = meanVisibility(raw.poses[0]);
    return {
      pose: raw.poses[0],
      diagnostics: {
        candidates: 1,
        pickedScore: score,
        runnerUpScore: null,
        pickedIndex: 0,
      },
    };
  }
  let bestIdx = 0;
  let bestScore = meanVisibility(raw.poses[0]);
  let secondScore = -Infinity;
  for (let i = 1; i < raw.poses.length; i += 1) {
    const s = meanVisibility(raw.poses[i]);
    if (s > bestScore) {
      secondScore = bestScore;
      bestScore = s;
      bestIdx = i;
    } else if (s > secondScore) {
      secondScore = s;
    }
  }
  return {
    pose: raw.poses[bestIdx],
    diagnostics: {
      candidates,
      pickedScore: bestScore,
      runnerUpScore: Number.isFinite(secondScore) ? secondScore : null,
      pickedIndex: bestIdx,
    },
  };
}

/**
 * Convenience wrapper that returns a RawPoseResult containing only the
 * picked primary pose (or empty when no pose is detected). Suitable as
 * a drop-in transform between any engine and any consumer that
 * expects single-subject input.
 */
export function pickPrimaryResult(raw: RawPoseResult): RawPoseResult {
  const { pose } = pickPrimaryRawPose(raw);
  return pose ? { poses: [pose] } : { poses: [] };
}
